import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signIn, resendVerificationEmail } from "../config/firebase";
import { useAuth } from "../context/AuthContext";
import { authErrorForUser } from "../utils/authUserMessages";
import { authLog } from "../utils/authDebugLog";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  const navigate = useNavigate();
  const { setCurrentUser } = useAuth();
  const submitLockRef = useRef(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    setError("");
    setLoading(true);
    setNeedsVerification(false);

    try {
      const result = await signIn(email, password);

      if (!result.success) {
        if (result.requiresVerification) {
          setNeedsVerification(true);
        }
        setError(authErrorForUser(result.error, "login"));
        setLoading(false);
        return;
      }

      authLog("login:navigateDashboard", {
        uidTail: result.user?.uid?.slice(-8),
      });
      setCurrentUser(result.user);
      setLoading(false);
      navigate("/dashboard");
    } catch (err) {
      setError(authErrorForUser(err, "login"));
      setLoading(false);
    } finally {
      submitLockRef.current = false;
    }
  };

  const handleResendVerification = async () => {
    try {
      const result = await resendVerificationEmail();
      if (result.success) {
        setVerificationSent(true);
        setError("");
      } else {
        setError(authErrorForUser(result.error, "login"));
      }
    } catch (err) {
      setError("Unable to send verification email. Please try again later.");
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>Sign In</h2>

        {error && (
          <div className="error-message">
            {error}
            {needsVerification && (
              <button
                onClick={handleResendVerification}
                className="link-button"
                disabled={verificationSent}
              >
                {verificationSent
                  ? "Verification email sent!"
                  : "Resend verification email"}
              </button>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
              disabled={loading}
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div className="auth-links">
          <Link to="/forgot-password">Forgot password?</Link>
          <p>
            Don't have an account? <Link to="/signup">Sign up</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
